import React, { Suspense, useEffect, useState } from 'react';
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';

// Theme
import { theme } from './theme';

// Import non-lazy components (always needed)
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { SnackbarProvider } from './components/Snackbar/SnackbarProvider';
import Layout from './components/Layout/Layout';
import RoleGuard from './components/RoleGuard/RoleGuard';
import PageLoader from './components/PageLoader/PageLoader';

// Import hooks
import { useAuthStore } from './store/authStore';

// Lazy-loaded pages (Code Splitting)
const LoginPage = React.lazy(() => import('./pages/Auth/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/Dashboard/DashboardPage'));
const OrdersPage = React.lazy(() => import('./pages/Orders/OrdersPage'));
const OrderDetailPage = React.lazy(() => import('./pages/Orders/OrderDetailPage'));
const OrderCreatePage = React.lazy(() => import('./pages/Orders/OrderCreatePage'));
const AvailabilityPage = React.lazy(() => import('./pages/Availability/AvailabilityPage'));
const CalendarPage = React.lazy(() => import('./pages/Calendar/CalendarPage'));
const PDFProcessingPage = React.lazy(() => import('./pages/PDFProcessing/PDFProcessingPage'));
const UsersPage = React.lazy(() => import('./pages/Users/UsersPage'));
const NotificationsPage = React.lazy(() => import('./pages/Notifications/NotificationsPage'));
const SettingsPage = React.lazy(() => import('./pages/Settings/SettingsPage'));
const TenantsPage = React.lazy(() => import('./pages/Tenants/TenantsPage'));
const ProfilePage = React.lazy(() => import('./pages/Profile/ProfilePage'));
const PublicBookingPage = React.lazy(() => import('./pages/Public/PublicBookingPage'));
const DataProtectionPage = React.lazy(() => import('./pages/DataProtection/DataProtectionPage'));
const RoutePage = React.lazy(() => import('./pages/Route/RoutePage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirects if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  const { initialize, user, tenant, isAuthenticated } = useAuthStore();
  const [router] = useState(() => createBrowserRouter(createRoutesFromElements(<>
                  {/* Public Routes */}
                  <Route
                    path="/login"
                    element={
                      <PublicRoute>
                        <LoginPage />
                      </PublicRoute>
                    }
                  />
                  <Route path="/booking/:token" element={<PublicBookingPage />} />
                  <Route path="/datenschutz" element={<DataProtectionPage />} />

                  {/* Protected Routes */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="dashboard" element={<DashboardPage />} />
                    <Route path="orders" element={<OrdersPage />} />
                    <Route path="orders/new" element={<OrderCreatePage />} />
                    <Route path="orders/:id" element={<OrderDetailPage />} />
                    <Route path="availability" element={<AvailabilityPage />} />
                    <Route path="calendar" element={<CalendarPage />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="route" element={<RoutePage />} />

                    {/* Admin-only Routes (ADMIN + GLOBAL_ADMIN) */}
                    <Route
                      path="pdf-processing"
                      element={
                        <RoleGuard roles={['ADMIN', 'AGENT', 'GLOBAL_ADMIN']}>
                          <PDFProcessingPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="users"
                      element={
                        <RoleGuard roles={['ADMIN', 'GLOBAL_ADMIN']}>
                          <UsersPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="notifications"
                      element={
                        <RoleGuard roles={['ADMIN', 'GLOBAL_ADMIN']}>
                          <NotificationsPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="settings"
                      element={
                        <RoleGuard roles={['ADMIN', 'GLOBAL_ADMIN']}>
                          <SettingsPage />
                        </RoleGuard>
                      }
                    />

                    {/* Global Admin-only Route */}
                    <Route
                      path="tenants"
                      element={
                        <RoleGuard roles={['GLOBAL_ADMIN']}>
                          <TenantsPage />
                        </RoleGuard>
                      }
                    />

                    {/* 404 innerhalb Layout (für eingeloggte Benutzer) */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>

                  {/* 404 Route für nicht-eingeloggte Benutzer */}
                  <Route path="*" element={<Navigate to="/login" replace />} />

  </>)));

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <SnackbarProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <Suspense fallback={<PageLoader />}>
              <RouterProvider key={`${isAuthenticated}:${user?.id}:${user?.role}:${tenant?.id}`} router={router} />
            </Suspense>
          </LocalizationProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
